import clsx from "clsx";
import React, { useEffect, useRef, useState } from "react";

import Button from "@material-ui/core/Button";
import DeleteIcon from "@material-ui/icons/Delete";
import EditIcon from "@material-ui/icons/Edit";
import WarningIcon from "@material-ui/icons/Warning";

import { racehorse360 } from "@tsg/1st-grpc-web-rh360";

import {
  ECommentDisplayingVariant,
  EReportersIcons,
  getReporterRoleIcon
} from "components/Comments/Comment/helper";
import HorseHeadInCircleIcon from "components/Icons/HorseHeadInCircle";
import UserIcon from "components/Icons/Profile";
import StethoscopeIcon from "components/Icons/Stethoscope";
import Loader from "components/Loader";
import { useLoggedInUser } from "components/LoggedInUserProvider";
import DeleteConfirmationDialog from "components/WorkoutComment/DeleteConfirmationDialog";
import { useRacehorse360Api } from "hooks/api";
import { transformViewDate } from "utils/date-utils";
import { getPersonFullName } from "utils/person";
import HorseGeneralVetCommentEditor from "../HorseGeneralVetCommentEditor";

import useStyles from "./styles";

interface IProps {
  isEditing: boolean;
  data: racehorse360.IHorseAggregatedComment;
  className?: string;
  onEditStart: (commentId: string) => void;
  onEditFinish: () => void;
  refetchHorseWarningMessage: () => void;
  refetchComments: () => void;
}

const HorseGeneralVetComment = (props: IProps) => {
  const {
    isEditing,
    data: {
      commenterId,
      commenterFirstName,
      commenterLastName,
      updatedOn,
      body: content,
      warningMessage,
      horseAggregatedCommentIconType: commentIconType,
      id
    },
    className,
    onEditStart,
    onEditFinish,
    refetchHorseWarningMessage,
    refetchComments
  } = props;
  const classes = useStyles();
  const commentInputRef = useRef<HTMLInputElement>(null);

  const { currentUser } = useLoggedInUser();
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);

  const canDoActionsOnComments = currentUser.rh360Id === commenterId;

  useEffect(() => {
    const input = commentInputRef.current;
    if (isEditing && input) {
      input?.focus();
      input.selectionStart = input.value.length;
    }
  }, [isEditing]);

  const { useDeleteHorseGeneralVetComment } = useRacehorse360Api();

  const {
    mutateAsync: deleteHorseGeneralVetComment,
    isPending: isLoadingDeleteHorseGeneralVetComment
  } = useDeleteHorseGeneralVetComment({
    onError: error => {
      console.error(error);
    }
  });

  const renderReporterIcon = (type: EReportersIcons) => {
    switch (type) {
      case EReportersIcons.VET:
        return <StethoscopeIcon className={classes.icon} />;
      case EReportersIcons.TRAINER:
        return <HorseHeadInCircleIcon className={classes.icon} />;
      case EReportersIcons.OTHER_USERS:
        return <UserIcon className={classes.icon} />;
    }
  };

  const handleDeleteClick = () => {
    setShowDeleteConfirmation(true);
  };

  const handleEditClick = () => {
    onEditStart(id);
  };

  const handleDeleteConfirm = async () => {
    setShowDeleteConfirmation(false);

    await deleteHorseGeneralVetComment({ id });

    warningMessage && refetchHorseWarningMessage();
    refetchComments();
  };

  const handleDeleteCancel = () => {
    setShowDeleteConfirmation(false);
  };

  const renderActionButtons = () => {
    return (
      canDoActionsOnComments &&
      !isEditing && (
        <div className={classes.commentActions}>
          <Button
            variant="outlined"
            className={classes.commentButton}
            onClick={handleDeleteClick}
          >
            <DeleteIcon className={classes.commentButtonIcon} />
          </Button>
          <Button
            variant="outlined"
            className={classes.commentButton}
            onClick={handleEditClick}
          >
            <EditIcon className={classes.commentButtonIcon} />
          </Button>
        </div>
      )
    );
  };

  const renderHeader = () => {
    return (
      <>
        <div className={classes.headerRow}>
          <span className={classes.commenterInfo}>
            {renderReporterIcon(
              getReporterRoleIcon(
                commentIconType,
                ECommentDisplayingVariant.COMMENT_FOR_VETERINARIAN
              )
            )}
            {getPersonFullName(
              { firstName: commenterFirstName, lastName: commenterLastName },
              "%f %M %L"
            )}
          </span>

          {renderActionButtons()}
        </div>
        <div className={classes.row}>
          <div className={classes.info}>
            <div className={classes.uppercase}>
              {transformViewDate(updatedOn, null, "PP")}
            </div>
            <div className={classes.additionalSigns}>&nbsp;&ndash;&nbsp;</div>
            <div>{transformViewDate(updatedOn, null, "p")}</div>
          </div>
        </div>
      </>
    );
  };

  const renderContent = () => {
    if (isEditing) {
      return (
        <HorseGeneralVetCommentEditor
          commentId={id}
          comment={content}
          warningComment={warningMessage}
          className={classes.editMode}
          refetchComments={refetchComments}
          refetchHorseWarningMessage={refetchHorseWarningMessage}
          onClose={onEditFinish}
        />
      );
    } else {
      return (
        <>
          {Boolean(warningMessage?.length) && (
            <div className={classes.warningComment}>
              <div className={classes.warningIconContainer}>
                <WarningIcon className={classes.warningIconColored} />
              </div>
              <div className={classes.warningMessage}>{warningMessage}</div>
            </div>
          )}
          <div className={classes.commentContent}>{content}</div>
        </>
      );
    }
  };

  return (
    <div className={clsx(classes.commentRoot, className)} tabIndex={0}>
      {isLoadingDeleteHorseGeneralVetComment && <Loader overlay />}

      {renderHeader()}
      {renderContent()}

      {showDeleteConfirmation && (
        <DeleteConfirmationDialog
          open={showDeleteConfirmation}
          onDelete={handleDeleteConfirm}
          onCancel={handleDeleteCancel}
        >
          {transformViewDate(updatedOn, null, "iiii, MMMM dd - h:mm a")}
        </DeleteConfirmationDialog>
      )}
    </div>
  );
};

export default React.memo(HorseGeneralVetComment);
